/**
 * Procedural audio. No sound files — every voice in this game is synthesised
 * from oscillators and shaped noise at the moment it is needed.
 *
 * The design is a direct consequence of the simulation's shape: the sim already
 * publishes a per-tick event list with world positions and magnitudes, so the
 * audio layer is a second consumer of the exact stream the renderer drains. It
 * knows nothing about the game and the game knows nothing about it.
 *
 * Three rules, borrowed from the art direction:
 *   1. The bed is low, cold and almost featureless — wind, a distant bell,
 *      crows. It should be possible to forget it is there.
 *   2. Combat is dry and short. A hit is a transient, not a tone.
 *   3. Only spells are allowed to be musical, and only briefly.
 */

import { EV, type SimEvent } from '../sim/events.ts';
import { Team, type UnitVoice } from '../sim/types.ts';
import { clamp, clamp01 } from '../core/math.ts';

/** Voices started in one frame beyond this are dropped, cheapest first. */
const MAX_VOICES_PER_FRAME = 7;
/** Two identical sounds inside this window collapse into one. */
const DEDUPE_MS = 45;
/** One confirmation for an input gesture, even if it orders a whole army. */
const ORDER_GAP_SECONDS = 0.16;
export type OrderFeedback = 'move' | 'attack' | 'invalid' | 'cancel';

function eventPriority(e: SimEvent): number {
  if (e.k === EV.StructureDown || e.k === EV.Breach || e.k === EV.Boss || e.k === EV.WaveStart || e.k === EV.WaveEnd) return 4;
  if (e.k === EV.Power || (e.k === EV.Impact && e.b >= 90) || (e.k === EV.Death && e.a >= 2)) return 3;
  if (e.k === EV.Impact || e.k === EV.StructureHit) return 2;
  return e.k === EV.Shoot ? 0 : 1;
}

export interface AudioSettings {
  master: number;
  effects: number;
  ambience: number;
  muted: boolean;
}

export class Audio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private fxBus!: GainNode;
  private ambBus!: GainNode;
  private noiseBuffer!: AudioBuffer;

  private droneOsc: OscillatorNode[] = [];
  private droneGain!: GainNode;
  private windGain!: GainNode;
  private windFilter!: BiquadFilterNode;
  private duckGain!: GainNode;

  private voicesThisFrame = 0;
  private lastPlayed = new Map<string, number>();
  private lastOrderAt = -Infinity;
  private started = false;

  readonly settings: AudioSettings = { master: 0.55, effects: 1, ambience: 0.8, muted: false };

  /**
   * Browsers will not start an AudioContext without a gesture, so this is
   * called from the first click or keypress rather than at boot.
   */
  start(): void {
    if (this.started) return;
    this.started = true;
    try {
      const Ctor: typeof AudioContext =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window.AudioContext ?? (window as any).webkitAudioContext) as typeof AudioContext;
      this.ctx = new Ctor();
    } catch {
      this.ctx = null;
      return;
    }
    const ctx = this.ctx;

    this.master = ctx.createGain();
    this.master.gain.value = this.settings.muted ? 0 : this.settings.master;
    this.master.connect(ctx.destination);

    this.fxBus = ctx.createGain();
    this.fxBus.gain.value = this.settings.effects;
    // Catch simultaneous impact peaks without flattening quiet details. A low
    // cut keeps long siege rumbles from accumulating inaudible sub-bass.
    const lowCut = ctx.createBiquadFilter();
    lowCut.type = 'highpass'; lowCut.frequency.value = 42; lowCut.Q.value = .55;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -14; limiter.knee.value = 12; limiter.ratio.value = 3;
    limiter.attack.value = .003; limiter.release.value = .14;
    this.fxBus.connect(lowCut).connect(limiter).connect(this.master);

    this.ambBus = ctx.createGain();
    this.ambBus.gain.value = this.settings.ambience;
    /**
     * The duck stage sits between the bed and the master, and it exists so
     * that ducking and the ambience *setting* never fight over one gain — the
     * settings write `ambBus.gain.value` directly, and a `setTargetAtTime`
     * ramp in flight on the same param would silently swallow the write.
     */
    this.duckGain = ctx.createGain();
    this.duckGain.gain.value = 1;
    this.ambBus.connect(this.duckGain).connect(this.master);

    this.noiseBuffer = this.makeNoise(2);
    this.buildBed();
  }

  /**
   * Push the ambient bed down and let it breathe back — a Starfall or a wall
   * coming down should own the air for a second.
   */
  private duckBed(depth: number, holdSeconds: number): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.duckGain.gain.setTargetAtTime(clamp01(1 - depth), now, 0.06);
    this.duckGain.gain.setTargetAtTime(1, now + holdSeconds, 0.7);
  }

  private makeNoise(seconds: number): AudioBuffer {
    const ctx = this.ctx as AudioContext;
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    // Cheap pink-ish noise: a few one-pole filters summed. Brown noise alone is
    // too muddy for wind and white is too hissy.
    let b0 = 0;
    let b1 = 0;
    let b2 = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.099;
      b1 = 0.963 * b1 + white * 0.2965;
      b2 = 0.57 * b2 + white * 1.0526;
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.16;
    }
    return buf;
  }

  /** The ambient bed: wind, a low drone, and the occasional far-off bell. */
  private buildBed(): void {
    const ctx = this.ctx as AudioContext;

    const wind = ctx.createBufferSource();
    wind.buffer = this.noiseBuffer;
    wind.loop = true;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'lowpass';
    this.windFilter.frequency.value = 420;
    this.windFilter.Q.value = 0.7;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0.12;
    wind.connect(this.windFilter).connect(this.windGain).connect(this.ambBus);
    wind.start();

    // A slow LFO on the filter so the wind breathes rather than hisses.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.06;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 180;
    lfo.connect(lfoGain).connect(this.windFilter.frequency);
    lfo.start();

    // The drone. Two detuned oscillators a fifth apart, very quiet, and it is
    // the thing that gets louder when a siege starts.
    this.droneGain = ctx.createGain();
    this.droneGain.gain.value = 0.0;
    const droneFilter = ctx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.value = 300;
    this.droneGain.connect(droneFilter).connect(this.ambBus);
    for (const [freq, detune] of [
      [55, 0],
      [55, -7],
      [82.4, 4],
    ] as const) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = freq;
      o.detune.value = detune;
      const g = ctx.createGain();
      g.gain.value = 0.3;
      o.connect(g).connect(this.droneGain);
      o.start();
      this.droneOsc.push(o);
    }
  }

  /** Call once per frame with the current tension, 0 (build) to 1 (a siege). */
  setTension(t: number): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const target = clamp01(t);
    this.droneGain.gain.setTargetAtTime(0.02 + target * 0.16, now, 1.6);
    this.windGain.gain.setTargetAtTime(0.1 + target * 0.06, now, 2.2);
    for (const o of this.droneOsc) o.detune.setTargetAtTime(o.detune.value * 0.9 + target * 9, now, 3);
  }

  setSettings(patch: Partial<AudioSettings>): void {
    Object.assign(this.settings, patch);
    if (!this.ctx) return;
    this.master.gain.value = this.settings.muted ? 0 : this.settings.master;
    this.fxBus.gain.value = this.settings.effects;
    this.ambBus.gain.value = this.settings.ambience;
  }

  beginFrame(): void {
    this.voicesThisFrame = 0;
  }

  /**
   * Call once after a local order is accepted (or a known refusal/cancel).
   * Pointer previews and individual companies in a group must not call this.
   * Input has a separate small rate limit so a loud battle cannot swallow it.
   */
  orderFeedback(kind: OrderFeedback): boolean {
    if (!this.ctx || this.settings.muted || this.settings.master <= 0 || this.settings.effects <= 0) return false;
    const now = this.ctx.currentTime;
    if (now - this.lastOrderAt < ORDER_GAP_SECONDS) return false;
    this.lastOrderAt = now;
    if (kind === 'move') {
      // A short wood-and-leather tap; quiet confirmation, not a musical note.
      this.noiseHit({gain:.225,duration:.055,freq:680,sweepTo:290,q:.7});
      this.tone({gain:.075,duration:.075,freq:190,to:95,type:'triangle'});
    } else if (kind === 'attack') {
      // A firmer double beat, with a restrained metallic edge.
      this.noiseHit({gain:.285,duration:.06,freq:1100,sweepTo:440,q:1});
      this.noiseHit({gain:.18,duration:.075,freq:780,sweepTo:250,q:.85,delay:.065});
      this.tone({gain:.096,duration:.11,freq:145,to:68,type:'triangle'});
    } else if (kind === 'invalid') {
      this.noiseHit({gain:.195,duration:.09,freq:340,sweepTo:120,q:.7});
      this.tone({gain:.09,duration:.13,freq:150,to:70,type:'triangle'});
    } else {
      this.noiseHit({gain:.15,duration:.05,freq:470,sweepTo:180,q:.6});
    }
    return true;
  }

  /**
   * Drain a tick's events. `gainFor` lets the caller attenuate by distance
   * from the camera, so a fight across the map is quieter than one at your
   * feet; `panFor` places it left or right of the glass the same way; and
   * `voiceOf` names what a struck unit is made of — a lookup the *caller*
   * provides, so this layer keeps knowing nothing about the game. All three
   * optional, and everything degrades to the old mono everyone-is-flesh mix.
   */
  consume(
    events: readonly SimEvent[],
    gainFor: (x: number, y: number) => number,
    panFor?: (x: number, y: number) => number,
    voiceOf?: (typeId: number) => UnitVoice,
  ): void {
    if (!this.ctx || this.settings.muted || this.settings.master <= 0 || this.settings.effects <= 0) return;
    // Event order is simulation order, not listening priority. A hundred arrow
    // releases must not spend the budget before a nearby gate falls. Sort a
    // private presentation list, preserving the original event stream.
    const audible = events.map((e,index) => ({e,index,g:clamp01(gainFor(e.x,e.y))}))
      .filter(p => p.g > .02 && p.e.k !== EV.Log)
      .sort((a,b) => eventPriority(b.e) - eventPriority(a.e) || b.g - a.g || a.index - b.index);
    for (const {e,g} of audible) {
      const p = panFor ? clamp(panFor(e.x,e.y),-1,1) : 0;
      switch (e.k) {
        case EV.Hit:
          this.hit(e.a, g, e.team === Team.Player, voiceOf ? voiceOf(e.b) : 'flesh', p);
          break;
        case EV.Block:
          this.block(g, p);
          break;
        case EV.Death:
          if (e.a >= 2) this.bigDeath(g, voiceOf ? voiceOf(e.b) : 'flesh', p);
          break;
        case EV.Shoot:
          this.shoot(e.a, g, p);
          break;
        case EV.Impact:
          this.impact(e.b, e.a, g, p, e.dx !== 0 || e.dy !== 0);
          break;
        case EV.StructureHit:
          this.structureHit(e.a, g, p);
          break;
        case EV.StructureDown:
        case EV.Breach:
          this.collapse(g, p);
          break;
        case EV.Power:
          this.cast(g, p);
          break;
        case EV.Boss:
        case EV.WaveStart:
          this.horn(0.9);
          break;
        case EV.WaveEnd:
          this.bell(0.7);
          break;
        case EV.Build:
          if (e.a === 1) this.thud(0.35 * g, 180, p);
          break;
        default:
          break;
      }
    }
  }

  // ── Voices ───────────────────────────────────────────────────────────────

  private take(key: string, cost = 1): boolean {
    if (!this.ctx || this.settings.muted || this.settings.master <= 0) return false;
    if (this.voicesThisFrame + cost > MAX_VOICES_PER_FRAME) return false;
    const now = this.ctx.currentTime * 1000;
    const last = this.lastPlayed.get(key) ?? -1e9;
    if (now - last < DEDUPE_MS) return false;
    this.lastPlayed.set(key, now);
    this.voicesThisFrame += cost;
    return true;
  }

  /**
   * Where a voice lands: the effects bus, through a stereo panner when the
   * caller placed it off-centre. A panner per voice rather than per bus,
   * because two fights on opposite flanks are the whole point of having one.
   * Pan is softened to ±0.75 so nothing sits entirely in one ear.
   */
  private sink(pan: number): AudioNode {
    const ctx = this.ctx as AudioContext;
    if (pan === 0 || typeof ctx.createStereoPanner !== 'function') return this.fxBus;
    const p = ctx.createStereoPanner();
    p.pan.value = pan * 0.75;
    p.connect(this.fxBus);
    return p;
  }

  /** A short shaped-noise burst — the workhorse for every impact. */
  private noiseHit(opts: {
    gain: number;
    duration: number;
    freq: number;
    q?: number;
    type?: BiquadFilterType;
    sweepTo?: number;
    pan?: number;
    delay?: number;
  }): void {
    const ctx = this.ctx as AudioContext;
    const now = ctx.currentTime + (opts.delay ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    const filt = ctx.createBiquadFilter();
    filt.type = opts.type ?? 'bandpass';
    filt.frequency.value = opts.freq;
    filt.Q.value = opts.q ?? 1.4;
    if (opts.sweepTo !== undefined) {
      filt.frequency.exponentialRampToValueAtTime(Math.max(40, opts.sweepTo), now + opts.duration);
    }
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(opts.gain, now + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);
    const output = this.sink(opts.pan ?? 0);
    src.connect(filt).connect(g).connect(output);
    src.onended = () => {
      src.disconnect(); filt.disconnect(); g.disconnect();
      if (output !== this.fxBus) output.disconnect();
    };
    src.start(now);
    src.stop(now + opts.duration + 0.02);
  }

  private tone(opts: {
    gain: number;
    duration: number;
    freq: number;
    to?: number;
    type?: OscillatorType;
    delay?: number;
    pan?: number;
  }): void {
    const ctx = this.ctx as AudioContext;
    const now = ctx.currentTime + (opts.delay ?? 0);
    const o = ctx.createOscillator();
    o.type = opts.type ?? 'sine';
    o.frequency.setValueAtTime(opts.freq, now);
    if (opts.to !== undefined) o.frequency.exponentialRampToValueAtTime(Math.max(20, opts.to), now + opts.duration);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(opts.gain, now + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);
    const output = this.sink(opts.pan ?? 0);
    o.connect(g).connect(output);
    o.onended = () => {
      o.disconnect(); g.disconnect();
      if (output !== this.fxBus) output.disconnect();
    };
    o.start(now);
    o.stop(now + opts.duration + 0.02);
  }

  /**
   * A blow landing, in the material of whoever caught it.
   *
   * Six recipes keyed on `UnitVoice` — every one is still rule 2's dry short
   * transient, and they differ the way a stick on a fence differs from a stick
   * on a gate: centre frequency, snap length, and what little tone rings
   * after. The dedupe key carries the voice, so a mixed melee — knights among
   * skeletons — keeps both textures instead of whichever struck first.
   */
  private hit(damage: number, gain: number, mine: boolean, voice: UnitVoice, pan: number): void {
    if (!this.take(`hit:${voice}`)) return;
    const heavy = clamp01(damage / 60);
    switch (voice) {
      case 'bone':
        // Dry sticks. No meat under it at all.
        this.noiseHit({ gain: gain * (0.15 + heavy * 0.14), duration: 0.05, freq: 1900, q: 2.6, sweepTo: 900, pan });
        this.tone({ gain: gain * 0.04, duration: 0.06, freq: 620, to: 240, type: 'square', pan });
        break;
      case 'metal':
        // A struck cuirass: the crack rings a moment.
        this.noiseHit({ gain: gain * (0.14 + heavy * 0.16), duration: 0.07, freq: 1400, q: 3, sweepTo: 800, pan });
        this.tone({ gain: gain * (0.04 + heavy * 0.04), duration: 0.18, freq: 340, to: 290, type: 'triangle', pan });
        break;
      case 'beast':
        // Into something with a lot behind it.
        this.noiseHit({ gain: gain * (0.2 + heavy * 0.26), duration: 0.12 + heavy * 0.1, freq: 210, sweepTo: 60, q: 0.7, type: 'lowpass', pan });
        this.tone({ gain: gain * (0.07 + heavy * 0.09), duration: 0.14, freq: 90, to: 38, type: 'triangle', pan });
        break;
      case 'timber':
        // An engine taking an axe: plank knock over a low creak.
        this.noiseHit({ gain: gain * (0.16 + heavy * 0.18), duration: 0.1, freq: 480, sweepTo: 140, q: 1.1, pan });
        this.tone({ gain: gain * 0.05, duration: 0.12, freq: 170, to: 80, type: 'triangle', pan });
        break;
      case 'spirit':
        // Barely material: air parting, a cold overtone, no thump.
        this.noiseHit({ gain: gain * (0.1 + heavy * 0.1), duration: 0.16, freq: 2600, q: 0.8, sweepTo: 4200, pan });
        this.tone({ gain: gain * 0.04, duration: 0.18, freq: 520, to: 880, type: 'sine', pan });
        break;
      default:
        // Meat and mail: a low thump under a short bright crack.
        this.noiseHit({
          gain: gain * (0.16 + heavy * 0.22),
          duration: 0.07 + heavy * 0.09,
          freq: 320 - heavy * 130,
          sweepTo: 90,
          q: 0.9,
          pan,
        });
        this.tone({
          gain: gain * (0.05 + heavy * 0.08),
          duration: 0.09,
          freq: mine ? 150 : 190,
          to: 60,
          type: 'triangle',
          pan,
        });
        break;
    }
  }

  private block(gain: number, pan = 0): void {
    if (!this.take('block')) return;
    this.noiseHit({ gain: gain * 0.18, duration: 0.075, freq: 2100, q: 3.2, sweepTo: 1100, pan });
    this.tone({ gain: gain * 0.045, duration: 0.11, freq: 760, to: 500, type: 'sine', pan });
  }

  private shoot(kind: number, gain: number, pan = 0): void {
    if (!this.take('shoot')) return;
    if (kind === 3) {
      // Handgonne: loud, dirty, short.
      this.noiseHit({ gain: gain * 0.35, duration: 0.16, freq: 900, sweepTo: 120, q: 0.6, pan });
    } else {
      this.noiseHit({ gain: gain * 0.1, duration: 0.055, freq: 2600, q: 2.2, sweepTo: 3600, pan });
    }
  }

  private impact(kind: number, radius: number, gain: number, pan = 0, directional = false): void {
    if (kind === 93 && !directional) {
      // Haul Them Down: chain, drag, and a crowd arriving where it did not
      // intend to. No bang — the power does no damage and must not sound as
      // though it did.
      if (!this.take('haul', 2)) return;
      this.noiseHit({ gain: gain * 0.3, duration: 0.5, freq: 320, sweepTo: 90, q: 0.7, type: 'lowpass', pan });
      this.tone({ gain: gain * 0.12, duration: 0.45, freq: 180, to: 60, type: 'triangle', pan });
      return;
    }
    if (kind === 90) {
      // Starfall. It owns the air: the bed ducks and breathes back.
      if (!this.take('starfall', 3)) return;
      this.duckBed(0.75, 1.2);
      this.noiseHit({ gain: gain * 0.5, duration: 1.1, freq: 220, sweepTo: 45, q: 0.5, type: 'lowpass', pan });
      this.tone({ gain: gain * 0.22, duration: 1.4, freq: 70, to: 26, type: 'sine', pan });
      this.tone({ gain: gain * 0.1, duration: 0.5, freq: 1200, to: 200, type: 'triangle', pan });
      return;
    }
    if (kind === 92) {
      if (!this.take('lightning', 2)) return;
      this.noiseHit({ gain: gain * 0.3, duration: 0.22, freq: 3000, q: 0.8, sweepTo: 600, pan });
      return;
    }
    if (kind === 93) {
      if (!this.take('charge', 2)) return;
      this.noiseHit({ gain: gain * 0.34, duration: 0.28, freq: 180, sweepTo: 60, q: 0.7, type: 'lowpass', pan });
      return;
    }
    if (!this.take('impact')) return;
    this.noiseHit({ gain: gain * (0.1 + clamp01(radius) * 0.12), duration: 0.12, freq: 700, sweepTo: 200, pan });
  }

  private structureHit(damage: number, gain: number, pan = 0): void {
    if (!this.take('struct')) return;
    this.noiseHit({
      gain: gain * clamp(0.08 + damage * 0.002, 0.06, 0.3),
      duration: 0.16,
      freq: 260,
      sweepTo: 80,
      q: 0.8,
      type: 'lowpass',
      pan,
    });
  }

  private collapse(gain: number, pan = 0): void {
    if (!this.take('collapse', 3)) return;
    this.duckBed(0.55, 0.9);
    this.noiseHit({ gain: gain * 0.45, duration: 1.3, freq: 400, sweepTo: 60, q: 0.4, type: 'lowpass', pan });
    this.tone({ gain: gain * 0.16, duration: 0.9, freq: 90, to: 32, type: 'sine', pan });
    this.noiseHit({ gain: gain * .13, duration: .38, freq: 1050, sweepTo: 310, q: .6, delay: .055, pan });
  }

  private bigDeath(gain: number, voice: UnitVoice = 'flesh', pan = 0): void {
    if (!this.take('bigdeath', 2)) return;
    if (voice === 'bone') {
      // A frame of dry sticks letting go all at once.
      this.noiseHit({ gain: gain * 0.28, duration: 0.4, freq: 1300, q: 1.6, sweepTo: 300, pan });
      return;
    }
    if (voice === 'timber' || voice === 'metal') {
      // An engine or a construct going over: junk, not meat.
      this.noiseHit({ gain: gain * 0.32, duration: 0.6, freq: 500, sweepTo: 90, q: 0.7, pan });
      this.tone({ gain: gain * 0.08, duration: 0.4, freq: 260, to: 100, type: 'square', pan });
      return;
    }
    this.noiseHit({ gain: gain * 0.3, duration: 0.5, freq: 300, sweepTo: 55, q: 0.6, type: 'lowpass', pan });
    this.tone({ gain: gain * 0.12, duration: 0.6, freq: 110, to: 40, type: 'triangle', pan });
  }

  private cast(gain: number, pan = 0): void {
    if (!this.take('cast', 2)) return;
    // The one place the game is allowed to be musical: a rising fifth.
    this.tone({ gain: gain * 0.12, duration: 0.45, freq: 220, to: 330, type: 'triangle', pan });
    this.tone({ gain: gain * 0.07, duration: 0.6, freq: 440, to: 660, type: 'sine', delay: 0.05, pan });
    this.noiseHit({ gain: gain * 0.1, duration: 0.35, freq: 1800, q: 1.2, sweepTo: 400, pan });
  }

  private thud(gain: number, freq: number, pan = 0): void {
    if (!this.take('thud')) return;
    this.noiseHit({ gain, duration: 0.13, freq, sweepTo: 60, q: 0.7, type: 'lowpass', pan });
  }

  /** The horn that opens a wave. Deliberately ugly. */
  private horn(gain: number): void {
    if (!this.take('horn', 3)) return;
    this.tone({ gain: gain * 0.16, duration: 1.5, freq: 110, to: 104, type: 'sawtooth' });
    this.tone({ gain: gain * 0.1, duration: 1.5, freq: 165, to: 156, type: 'sawtooth', delay: 0.02 });
    this.tone({ gain: gain * 0.05, duration: 1.2, freq: 55, type: 'square', delay: 0.1 });
  }

  /** The bell that closes one. Nine bells were cast; this is one of them. */
  private bell(gain: number): void {
    if (!this.take('bell', 3)) return;
    for (const [mul, g, dur] of [
      [1, 0.16, 3.4],
      [2.76, 0.07, 2.2],
      [5.4, 0.035, 1.4],
      [8.9, 0.02, 0.9],
    ] as const) {
      this.tone({ gain: gain * g, duration: dur, freq: 196 * mul, type: 'sine' });
    }
  }

  /** A crow, or a far-off bell. Called occasionally by the caller. */
  ambientAccent(kind: 'crow' | 'bell'): void {
    if (!this.ctx) return;
    if (kind === 'bell') {
      this.bell(0.22);
      return;
    }
    if (!this.take('crow', 2)) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    for (let n = 0; n < 2; n++) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      const t = now + n * 0.19;
      o.frequency.setValueAtTime(760, t);
      o.frequency.exponentialRampToValueAtTime(420, t + 0.13);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.035, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 1200;
      f.Q.value = 2;
      o.connect(f).connect(g).connect(this.ambBus);
      o.start(t);
      o.stop(t + 0.2);
    }
  }

  get running(): boolean {
    return this.ctx !== null;
  }
}
